// src/components/themetoggle.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTheme } from '@hooks';

/* The theme control.
   ------------------------------------------------------------------------
   One button, two icons, and the icon shown is the theme you will get rather
   than the one you are in. A control that displays the current state makes
   the reader work out what pressing it does; a control that displays the
   destination is read in one glance.

   Both icons are always in the DOM and are crossfaded, so the swap is a
   rotation rather than a substitution and there is no reflow. Rendering only
   the active one would also make the button's content depend on state that
   arrives after hydration, which is a mismatch waiting to happen.

   The icons are drawn here rather than in components/icons because neither is
   used anywhere else and both are four elements. */

const StyledToggle = styled.button`
  ${({ theme }) => theme.mixins.flexCenter};
  position: relative;
  padding: 0;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  color: var(--text-emphasis);
  transition: var(--transition);

  &:hover,
  &:focus-visible {
    color: var(--accent);
    background-color: var(--accent-tint);
  }

  /* Stacked, so the box never resizes and the two icons rotate through each
     other rather than one replacing the other. */
  svg {
    position: absolute;
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: opacity 0.25s var(--easing), transform 0.35s var(--easing);
  }

  .sun {
    opacity: ${({ $showSun }) => ($showSun ? 1 : 0)};
    transform: rotate(${({ $showSun }) => ($showSun ? '0deg' : '-70deg')})
      scale(${({ $showSun }) => ($showSun ? 1 : 0.6)});
  }

  .moon {
    opacity: ${({ $showSun }) => ($showSun ? 0 : 1)};
    transform: rotate(${({ $showSun }) => ($showSun ? '70deg' : '0deg')})
      scale(${({ $showSun }) => ($showSun ? 0.6 : 1)});
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

const ThemeToggle = ({ className }) => {
  const [theme, toggleTheme] = useTheme();
  const isDark = theme !== 'light';

  return (
    <StyledToggle
      type="button"
      className={className}
      onClick={toggleTheme}
      $showSun={isDark}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Light theme' : 'Dark theme'}>
      <svg className="sun" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2.4v2.4M12 19.2v2.4M4.2 12H1.8M22.2 12h-2.4M6.5 6.5 4.8 4.8M19.2 19.2l-1.7-1.7M17.5 6.5l1.7-1.7M4.8 19.2l1.7-1.7" />
      </svg>

      <svg className="moon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20.5 14.3A8.6 8.6 0 0 1 9.7 3.5a8.6 8.6 0 1 0 10.8 10.8Z" />
      </svg>
    </StyledToggle>
  );
};

ThemeToggle.propTypes = {
  className: PropTypes.string,
};

export default ThemeToggle;
